// -- == //
import { Pagination } from 'antd';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import * as S from './styles';
import Empty from 'components/empty';
import Skeleton from 'components/skeleton';

interface IProps {
  altanfeethiFields: any;
  headers: IPropsHeader[];
  data: any[];
  style?: object;
  totlaItems?: number;
  pagination?: boolean;
  headerStyle?: 'two' | 'one';
  headerColor?: string;
  loading?: boolean;
  lastItemRef?: (node: HTMLElement | null) => void;
  /**
   * Shown instead of the table body when `data` is empty and nothing is
   * loading. Defaults to a generic `Empty`; pass a screen-specific one, or
   * `null` to keep the old bare-headers behaviour.
   */
  empty?: ReactNode;
}

interface IPropsHeader {
  id: string | number;
  title: string;
  icon?: string;
  dir?: 'left' | 'right';
  /**
   * Column width. The table lays out `fixed`, so this is the width the column
   * gets rather than a suggestion the content can overrule.
   *
   * Give it to every predictable column — identifier, status, count, date,
   * actions — sized to what that column holds, and leave it off the one or
   * two columns carrying the real payload (title, subject, name,
   * description). Those share whatever is left, at every viewport.
   */
  width?: string | number;
  padding?: string;
  /**
   * Name of the `altanfeethiFields` entry that fills this column.
   *
   * Without it, cells are matched to headers by array position — which holds
   * only for as long as the two lists stay the same length and the same
   * order. Give every header a key on any table you touch.
   */
  key?: string;
  /**
   * What the column holds. Drives alignment and cell typography:
   *
   *   num      right aligned, mono, tabular numerals
   *   actions  right aligned, always visible, quiet until hovered
   *
   * `dir` still decides for columns that declare no type.
   */
  type?: 'text' | 'num' | 'actions';
}

const alignOf = (header?: IPropsHeader): 'left' | 'right' | 'center' => {
  if (!header) return 'center';
  if (header.type === 'num' || header.type === 'actions') return 'right';
  return header.dir || 'center';
};

const classOf = (header?: IPropsHeader) => `align-${alignOf(header)}${header?.type ? ` ${header.type}` : ''}`;

/** Rows to draw while loading when there is no previous page to hold the height. */
const SKELETON_ROWS = 6;

/**
 * A body cell.
 *
 * Cells are one line tall and clip with an ellipsis, so anything that does not
 * fit would otherwise be silently unreadable. This measures the rendered cell
 * and, when it is actually clipped, puts its full text on `title` — the value
 * stays one hover away without a second component or a per-table change.
 *
 * `generation` is bumped by AppTable on resize; it exists only to re-run the
 * measurement when the column widths move.
 */
const Cell: FC<{ className: string; generation: number; children: ReactNode }> = ({
  className,
  generation,
  children
}) => {
  const ref = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    const cell = ref.current;
    if (!cell) return;

    // The call sites render either a bare value or one wrapping <div>; the
    // ellipsis lands on whichever of the two is the block that overflows.
    const probe = (cell.firstElementChild as HTMLElement) || cell;
    const full = (cell.textContent || '').trim();

    if (full && probe.scrollWidth > probe.clientWidth + 1) cell.setAttribute('title', full);
    else cell.removeAttribute('title');
  }, [generation, children]);

  return (
    <S.Td ref={ref} className={className}>
      {children}
    </S.Td>
  );
};

const defaultEmpty = (
  <Empty title="Nothing to show" text="No records here yet. They appear in this table as they are created." />
);

const AppTable: FC<IProps> = ({
  altanfeethiFields,
  data,
  headers,
  style,
  pagination,
  totlaItems,
  headerStyle = 'one',
  headerColor,
  loading,
  lastItemRef,
  empty
}) => {
  const [page, setPage] = useState<number>(1);

  /**
   * Column widths move whenever the table does — the viewport, but also the
   * sidebar collapsing beside it — and with them what does and does not fit.
   * One observer on the table re-measures every cell; nothing else reads this.
   */
  const container = useRef<HTMLDivElement>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!container.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => setGeneration((n) => n + 1));
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  const handleChnage = (newPage: number) => {
    setPage(newPage);
  };

  //

  const hasRows = Array.isArray(data) && data.length > 0;
  const emptyState = empty === undefined ? defaultEmpty : empty;
  const showEmpty = !loading && !hasRows && emptyState !== null;

  /**
   * One entry per rendered cell.
   *
   * Keyed tables follow the header order and look each cell up by name. The
   * index fallback reproduces the old behaviour exactly, including the tables
   * whose field object and header list have drifted out of step.
   */
  const fieldList: any[] = Object.values(altanfeethiFields || {});
  const keyed = Array.isArray(headers) && headers.some((header) => header?.key);
  const columns: { header?: IPropsHeader; render: any }[] = keyed
    ? headers.map((header, index) => ({
        header,
        render: (header.key && altanfeethiFields?.[header.key]) || fieldList[index]
      }))
    : fieldList.map((render, index) => ({ header: headers?.[index], render }));

  /**
   * While loading, skeleton rows stand in for the body — they hold the row
   * height and the column widths, so nothing jumps when the data lands. On a
   * page change they replace the old rows rather than sitting under them, and
   * the count matches the page being replaced so the table keeps its height.
   */
  const skeletonRows = hasRows ? data.length : SKELETON_ROWS;

  return (
    <>
      <S.TableCon ref={container}>
        {showEmpty ? (
          emptyState
        ) : (
          <S.Table style={style}>
            <S.Thead className={headerStyle}>
              <S.Tr>
                {headers?.map((item: IPropsHeader, index) => (
                  <S.Th
                    key={index}
                    className={classOf(item)}
                    style={{ width: item.width, padding: item.padding, color: headerColor }}
                  >
                    <S.HeaderTh
                      key={item.id}
                      style={{
                        justifyContent:
                          alignOf(item) === 'right' ? 'flex-end' : alignOf(item) === 'left' ? 'flex-start' : 'center'
                      }}
                    >
                      {item.icon ? (
                        <ReactSVG
                          src={item.icon}
                          beforeInjection={(svg) => {
                            svg.setAttribute('stroke', headerColor || 'currentColor');
                          }}
                        />
                      ) : (
                        <></>
                      )}
                      {item.title}
                    </S.HeaderTh>
                  </S.Th>
                ))}
              </S.Tr>
            </S.Thead>
            <S.Tbody>
              {loading
                ? Array.from({ length: skeletonRows }).map((_, index) => (
                    <S.Tr key={`skeleton-${index}`}>
                      {columns.map((column, index2) => (
                        <S.Td key={`skeleton-td-${index2}`} className={`align-${alignOf(column.header)}`}>
                          <Skeleton />
                        </S.Td>
                      ))}
                    </S.Tr>
                  ))
                : hasRows &&
                  data.map((item: any, index) => {
                    const isLast = index === data.length - 1;
                    return (
                      <S.Tr key={index} ref={isLast ? lastItemRef || null : null}>
                        {columns.map((column, index2) => {
                          const cell = typeof column.render === 'function' ? column.render(item) : <></>;
                          return (
                            <Cell key={`td-${index2}`} className={classOf(column.header)} generation={generation}>
                              {column.header?.type === 'actions' ? <S.RowActions>{cell}</S.RowActions> : cell}
                            </Cell>
                          );
                        })}
                      </S.Tr>
                    );
                  })}
            </S.Tbody>
          </S.Table>
        )}
      </S.TableCon>
      {pagination && hasRows ? (
        <S.Pagination>
          <Pagination current={page} onChange={handleChnage} total={totlaItems} showSizeChanger={false} />
        </S.Pagination>
      ) : (
        <></>
      )}
    </>
  );
};

export default AppTable;
